// ./src/utils/ticketRemoval.ts
//
// ROADMAP #187 — the parts of a removal of tickets / guests that are not drawing (`TicketRemovalDrawer` draws them):
// reading the plan for a long selection in several calls, and running the removal until the server says nothing is
// left. Free of React so `npm run check-ticket-removal` RUNS them.
import type { OpResult } from "@sdflc/api-helpers";

import type { TicketRemovalRefund, TicketsRefunded, TicketsRefundInput, TicketsRemovalInput, TicketsRemovalPlan, TicketsRemoved } from "./api/EventTicketService";

/**
 * Ids per request — the server's limit. A selection up to it goes in ONE plan and ONE removal (repeated while orders are
 * left), so an order is never split between two plans (which would understate its refund and refund it in two parts).
 * Only Clear guests on a very large event goes past it; the server bounds the money work per call either way.
 */
export const REMOVAL_IDS_PER_CALL = 1000;

export const removalChunks = (ids: string[]): string[][] => {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += REMOVAL_IDS_PER_CALL) {
    out.push(ids.slice(i, i + REMOVAL_IDS_PER_CALL));
  }

  return out;
};

/** One plan for a selection read in several calls: money and counts add up; ONE call that may not decide decides it. */
export const mergeRemovalPlans = (plans: TicketsRemovalPlan[]): TicketsRemovalPlan => ({
  ticketCount: plans.reduce((s, p) => s + (Number(p.ticketCount) || 0), 0),
  placeCount: plans.reduce((s, p) => s + (Number(p.placeCount) || 0), 0),
  refundableAmount: Math.round(plans.reduce((s, p) => s + (Number(p.refundableAmount) || 0), 0) * 100) / 100,
  currencyCode: plans.find((p) => p.currencyCode)?.currencyCode ?? null,
  needsRefundChoice: plans.some((p) => p.needsRefundChoice),
  mayDecideRefund: plans.every((p) => p.mayDecideRefund),
  blockedReason: plans.find((p) => p.blockedReason)?.blockedReason ?? null,
  blockedMessage: plans.find((p) => p.blockedMessage)?.blockedMessage ?? null,
  holderGuestCount: plans.reduce((s, p) => s + (Number(p.holderGuestCount) || 0), 0),
  otherHolderNames: Array.from(new Set(plans.flatMap((p) => p.otherHolderNames ?? []))),
  orders: plans.flatMap((p) => p.orders ?? []),
});

/** The ids of a removal, chunked, each as the input of one call (tickets OR guests — the drawer is given one kind). */
export const removalInputs = (eventId: string, ticketIds: string[], eventGuestIds: string[], refund: TicketRemovalRefund | null): TicketsRemovalInput[] =>
  ticketIds.length > 0
    ? removalChunks(ticketIds).map((ids) => ({ eventId, ticketIds: ids, refund }))
    : removalChunks(eventGuestIds).map((ids) => ({ eventId, eventGuestIds: ids, refund }));

export interface TicketRemovalRun {
  /** What was done — all of it, or what was done before `refused`. */
  outcome: TicketsRemoved;
  refused: OpResult | null;
}

/**
 * Runs a removal to the end. The server refunds a few orders per call and says how many are left (`remainingOrders`), so
 * the SAME call is repeated until nothing is left — and stopped at the first refusal (what came before it stays done,
 * and is reported), or when a call moved nothing (never loop on a server that cannot progress).
 */
export const runTicketRemoval = async (
  inputs: TicketsRemovalInput[],
  call: (input: TicketsRemovalInput) => Promise<OpResult>,
  onStep?: (soFar: TicketsRemoved) => void,
): Promise<TicketRemovalRun> => {
  const outcome: TicketsRemoved = { removedTicketIds: [], removedGuestIds: [], refundedOrderIds: [], refundedAmount: 0, currencyCode: null, remainingOrders: 0 };

  for (const input of inputs) {
    for (;;) {
      const result = await call(input);
      const step = (result.getDataFirst() as TicketsRemoved | null) ?? null;
      if (step) {
        outcome.removedTicketIds.push(...(step.removedTicketIds ?? []));
        outcome.removedGuestIds.push(...(step.removedGuestIds ?? []));
        outcome.refundedOrderIds.push(...(step.refundedOrderIds ?? []));
        outcome.refundedAmount = Math.round((outcome.refundedAmount + (Number(step.refundedAmount) || 0)) * 100) / 100;
        outcome.currencyCode = step.currencyCode ?? outcome.currencyCode;
        onStep?.(outcome);
      }

      if (!result.didSucceed()) {
        return { outcome, refused: result };
      }

      const left = Number(step?.remainingOrders) || 0;
      const moved = (step?.refundedOrderIds?.length ?? 0) + (step?.removedTicketIds?.length ?? 0) + (step?.removedGuestIds?.length ?? 0) > 0;
      if (left === 0 || !moved) {
        break;
      }
    }
  }

  return { outcome, refused: null };
};

export interface TicketRefundRun {
  /** What was refunded — all of it, or what was before `refused`. */
  outcome: TicketsRefunded;
  refused: OpResult | null;
}

/**
 * The Box Office's bulk refund, run to the end: the server refunds a few orders per call (one refund per order) and says
 * how many are left, so the same call is repeated — stopped at the first refusal, or when a call moved nothing.
 */
export const runTicketRefund = async (
  eventId: string,
  ticketIds: string[],
  call: (input: TicketsRefundInput) => Promise<OpResult>,
): Promise<TicketRefundRun> => {
  const outcome: TicketsRefunded = { refundedTicketIds: [], refundedOrderIds: [], refundedAmount: 0, currencyCode: null, remainingOrders: 0 };

  for (const ids of removalChunks(ticketIds)) {
    for (;;) {
      const result = await call({ eventId, ticketIds: ids });
      const step = (result.getDataFirst() as TicketsRefunded | null) ?? null;
      if (step) {
        outcome.refundedTicketIds.push(...(step.refundedTicketIds ?? []));
        outcome.refundedOrderIds.push(...(step.refundedOrderIds ?? []));
        outcome.refundedAmount = Math.round((outcome.refundedAmount + (Number(step.refundedAmount) || 0)) * 100) / 100;
        outcome.currencyCode = step.currencyCode ?? outcome.currencyCode;
      }

      if (!result.didSucceed()) {
        return { outcome, refused: result };
      }

      const moved = (step?.refundedOrderIds?.length ?? 0) + (step?.refundedTicketIds?.length ?? 0) > 0;
      if ((Number(step?.remainingOrders) || 0) === 0 || !moved) {
        break;
      }
    }
  }

  return { outcome, refused: null };
};
